import React from 'react';
import useInView from '../hooks/useInView';
import { FEATURES, IMAGES } from '../data';

const FeaturesSection = ({ dark, t }) => {
  const [ref, inView] = useInView(0.1);

  return (
    <section
      id="features"
      style={{ padding: '120px 32px', background: t.bg, position: 'relative', overflow: 'hidden' }}
    >
      {/* Large decorative "F" */}
      <div
        style={{
          position: 'absolute', right: -20, top: '50%', transform: 'translateY(-50%)',
          fontFamily: "'Cormorant Garamond',serif", fontSize: '40vw', fontWeight: 700,
          color: dark ? 'rgba(201,162,39,0.02)' : 'rgba(201,162,39,0.04)',
          lineHeight: 1, userSelect: 'none', pointerEvents: 'none', zIndex: 0,
        }}
      >
        F
      </div>

      <div ref={ref} style={{ maxWidth: 1280, margin: '0 auto', position: 'relative', zIndex: 1 }}>
        <div className="two-col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 80, alignItems: 'center' }}>

          {/* Left: Image */}
          <div
            style={{
              position: 'relative',
              opacity: inView ? 1 : 0,
              animation: inView ? 'slideRight 0.8s 0.2s cubic-bezier(.16,1,.3,1) forwards' : 'none',
            }}
          >
            <img
              src={IMAGES.team}
              alt="Our professional team"
              style={{
                width: '100%', height: 480, objectFit: 'cover', borderRadius: 20,
                border: `1px solid ${t.border}`,
                boxShadow: dark ? '0 24px 80px rgba(0,0,0,0.6)' : '0 24px 80px rgba(0,0,0,0.12)',
              }}
            />
            <div
              style={{
                position: 'absolute', bottom: 0, left: 0, right: 0, height: '35%',
                background: 'linear-gradient(to top,rgba(201,162,39,0.12),transparent)',
                borderRadius: '0 0 20px 20px',
              }}
            />
            {/* Badge */}
            <div
              style={{
                position: 'absolute', bottom: -24, right: -24,
                background: dark ? 'rgba(14,14,14,0.97)' : 'rgba(255,255,255,0.97)',
                border: `2px solid ${t.gold}`,
                borderRadius: 16, padding: '18px 22px', textAlign: 'center',
                backdropFilter: 'blur(20px)',
                boxShadow: dark ? '0 16px 40px rgba(0,0,0,0.5)' : '0 16px 40px rgba(0,0,0,0.12)',
              }}
            >
              <div style={{ fontSize: 30, marginBottom: 6 }}>🌍</div>
              <div style={{ fontFamily: "'Cormorant Garamond',serif", fontWeight: 700, fontSize: 18, color: t.gold }}>
                Pan-India
              </div>
              <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 12, color: t.textMuted }}>
                Villages to Metros
              </div>
            </div>
          </div>

          {/* Right: Feature list */}
          <div
            style={{
              opacity: inView ? 1 : 0,
              animation: inView ? 'slideLeft 0.8s 0.2s cubic-bezier(.16,1,.3,1) forwards' : 'none',
            }}
          >
            <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 12, color: t.gold, letterSpacing: 3, textTransform: 'uppercase', marginBottom: 16, fontWeight: 600 }}>
              — Why Choose Us —
            </div>
            <h2 style={{ fontFamily: "'Cormorant Garamond',serif", fontWeight: 700, fontSize: 'clamp(2rem,4vw,3rem)', color: t.text, marginBottom: 16 }}>
              The Future Fund <span className="gold-text">Advantage</span>
            </h2>
            <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 16, color: t.textSub, lineHeight: 1.75, marginBottom: 44 }}>
              We've built a loan process that respects your time, simplifies paperwork, and gets funds to you faster than traditional banks.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              {FEATURES.map((f, i) => (
                <div
                  key={f.title}
                  style={{
                    display: 'flex', gap: 18, alignItems: 'flex-start',
                    padding: '22px 26px',
                    background: t.surfaceB,
                    border: `1px solid ${t.border}`,
                    borderRadius: 14,
                    transition: 'all 0.3s',
                    cursor: 'default',
                    opacity: inView ? 1 : 0,
                    animation: inView ? `fadeUp 0.5s ${0.3 + i * 0.1}s cubic-bezier(.16,1,.3,1) forwards` : 'none',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = t.gold;
                    e.currentTarget.style.transform = 'translateX(6px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = t.border;
                    e.currentTarget.style.transform = 'translateX(0)';
                  }}
                >
                  <div
                    style={{
                      width: 50, height: 50, borderRadius: 12, flexShrink: 0,
                      background: 'linear-gradient(135deg,rgba(201,162,39,0.15),rgba(232,192,64,0.1))',
                      border: '1px solid rgba(201,162,39,0.3)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 22,
                    }}
                  >
                    {f.icon}
                  </div>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 5 }}>
                      <span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 11, color: t.gold, fontWeight: 700, letterSpacing: 1 }}>
                        {f.no}
                      </span>
                      <h4 style={{ fontFamily: "'Cormorant Garamond',serif", fontWeight: 700, fontSize: 18, color: t.text, margin: 0 }}>
                        {f.title}
                      </h4>
                    </div>
                    <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 14, color: t.textSub, lineHeight: 1.65, margin: 0 }}>
                      {f.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;
